import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Industries landing page component
 * Showcases all industries that Raidu serves with aesthetic cards and animations
 * @returns {JSX.Element} The rendered Industries component
 */
const Industries: React.FC = () => {
  // State for tracking which industry card is hovered
  const [hoveredIndustry, setHoveredIndustry] = useState<string | null>(null);
  
  // State for tracking if component is mounted
  const [isMounted, setIsMounted] = useState<boolean>(false);
  
  // Effect to handle component mount
  useEffect(() => {
    try {
      // Log component mount
      console.log('[Industries] Component mounted');
      setIsMounted(true);
      
      // Cleanup function
      return () => {
        console.log('[Industries] Component unmounted');
      };
    } catch (error) {
      // Log any errors in the effect
      console.error('[Industries] Error in component effect:', error);
    }
  }, []);

  // Industry data with images, descriptions, and links
  const industries = [
    {
      id: 'healthcare',
      name: 'Healthcare',
      description: 'AI solutions for healthcare providers to improve patient outcomes, streamline operations, and enhance clinical decision-making.',
      image: '/images/industries/healthcare.jpg',
      color: 'from-blue-500 to-cyan-400',
      icon: '🏥',
    },
    {
      id: 'finance',
      name: 'Finance',
      description: 'Secure AI implementations for financial institutions to detect fraud, automate processes, and deliver personalized customer experiences.',
      image: '/images/industries/finance.jpg',
      color: 'from-emerald-500 to-green-400',
      icon: '💹',
    },
    {
      id: 'legal',
      name: 'Legal',
      description: 'AI-powered tools for law firms to streamline document review, enhance legal research, and improve client services.',
      image: '/images/industries/legal.jpg',
      color: 'from-amber-500 to-yellow-400',
      icon: '⚖️',
    },
    {
      id: 'education',
      name: 'Education',
      description: 'Innovative AI solutions for educational institutions to personalize learning, automate administrative tasks, and improve student outcomes.',
      image: '/images/industries/education.jpg',
      color: 'from-red-500 to-rose-400',
      icon: '🎓',
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing',
      description: 'AI-driven optimization for manufacturing processes, predictive maintenance, and supply chain management.',
      image: '/images/industries/manufacturing.jpg',
      color: 'from-indigo-500 to-purple-400',
      icon: '🏭',
    },
    {
      id: 'retail',
      name: 'Retail',
      description: 'AI solutions for retailers to enhance customer experiences, optimize inventory, and deliver personalized recommendations.',
      image: '/images/industries/retail.jpg',
      color: 'from-pink-500 to-fuchsia-400',
      icon: '🛍️',
    },
  ];

  try {
    return (
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          {/* Header section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industry Solutions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Raidu delivers tailored AI solutions across diverse industries, addressing unique challenges with secure, private, and efficient implementations.
            </p>
          </div>
          
          {/* Industry cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => (
              <Link
                key={industry.id}
                to={`/industries/${industry.id}`}
                className={`
                  group relative overflow-hidden rounded-xl shadow-lg transition-all duration-300 
                  ${hoveredIndustry === industry.id ? 'scale-[1.02]' : 'scale-100'}
                  hover:shadow-xl
                `}
                onMouseEnter={() => setHoveredIndustry(industry.id)}
                onMouseLeave={() => setHoveredIndustry(null)}
                onClick={() => {
                  try {
                    console.log(`[Industries] User clicked on ${industry.name} industry card`);
                  } catch (error) {
                    console.error(`[Industries] Error handling click on ${industry.name}:`, error);
                  }
                }}
              >
                {/* Card background with gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br bg-opacity-80 transition-all duration-300 z-10"></div>
                
                {/* Card image */}
                <div className="absolute inset-0 bg-gray-900">
                  <div 
                    className={`
                      absolute inset-0 bg-gradient-to-br ${industry.color} opacity-90
                      transition-all duration-500
                      ${hoveredIndustry === industry.id ? 'opacity-95' : 'opacity-90'}
                    `}
                  ></div>
                </div>
                
                {/* Card content */}
                <div className="relative z-20 p-6 h-full flex flex-col justify-between min-h-[320px]">
                  <div className="mb-4">
                    <span className="text-4xl mb-4 block">{industry.icon}</span>
                    <h3 className="text-2xl font-bold text-white mb-2">{industry.name}</h3>
                    <p className="text-white/90">{industry.description}</p>
                  </div>
                  
                  <div className="mt-4">
                    <span className="inline-flex items-center text-white font-medium">
                      Learn more
                      <svg 
                        className={`ml-2 w-5 h-5 transition-transform duration-300 ${hoveredIndustry === industry.id ? 'translate-x-1' : ''}`} 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* CTA section */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Don't see your industry?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Our AI solutions are adaptable to any industry. Contact us to discuss your specific needs.
            </p>
            <Link
              to="/company/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              onClick={() => {
                try {
                  console.log('[Industries] User clicked contact us CTA');
                } catch (error) {
                  console.error('[Industries] Error handling contact CTA click:', error);
                }
              }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('[Industries] Error rendering component:', error);
    return (
      <div className="py-16 px-4 text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
        <p className="text-gray-700">An error occurred while rendering the Industries page.</p>
      </div>
    );
  }
};

export default Industries;
